import { Box, Button, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ECInboxList } from './ECInboxList';
import { ECEmailsList, EmailCard } from './ECEmailsList';
import { ECEmailConversation } from './ECEmailConversation';
import { tokens } from '../../global/theme/tokens';
import { Fragment, useEffect, useRef, useState } from 'react';
import ExoDialog from '../exo/ExoDialog';
import { NewEmailIntegration } from '../new/NewEmailIntegration';
import { updateOrCreate } from '../special/updateOrCreate';
import DeleteIcon from '@mui/icons-material/Delete';
import InboxIcon from '@mui/icons-material/Inbox';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import DraftsIcon from '@mui/icons-material/Drafts';
import InventoryIcon from '@mui/icons-material/Inventory';
import { useTranslation } from 'react-i18next';
import { API_POST, API_DELETE, DBRequest, useConfig } from '../../api/api';
import { NewEmail } from '../new/NewEmail';
import ExoDrawer from '../exo/ExoDrawer';
import Div100vh from 'react-div-100vh';
import { ECSettings, EC_SETTINGS_TABS } from './ECSettings';
import { ECUndoSend } from './ECUndoSend';
import { useEmailClient } from './useEmailClient';
import AddIcon from '@mui/icons-material/Add';
import {
  pointerWithin,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import ExoAlert from '../exo/ExoAlert';
import { useNavigate } from 'react-router-dom';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAiPageContext } from '../ai-chat';

export const EmailClient = ({ debug, emailId, isStandalone = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [activeItem, setActiveItem] = useState(null);

  const { emailCredentials, setEmailCredentials, fetchFolders } =
    useEmailClient();

  const emailListRef = useRef(null);
  const emailConversationRef = useRef(null);

  const [openNewEmail, setOpenNewEmail] = useState(null);

  const [openSettings, setOpenSettings] = useState(false);
  // Which tab the settings dialog opens on — the inbox list can jump straight
  // to out of office.
  const [settingsTab, setSettingsTab] = useState(EC_SETTINGS_TABS.accounts);

  function handleOpenSettings(tab = EC_SETTINGS_TABS.accounts) {
    setSettingsTab(tab);
    setOpenSettings(true);
  }

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);

  // Only emails served by the imap-emails API are ImapEmails the AI chat can
  // reference. The Outbound and AI Drafts folders list OutboundEmail and
  // AiEmailDraft records, whose ids would point at unrelated inbox mails, so
  // no page context is registered there.
  const isImapEmailFolder =
    !selectedFolder?.api || Boolean(selectedFolder?.useDefaultApiForShow);

  useAiPageContext(
    selectedEmail &&
      isImapEmailFolder && {
        type: 'imap_email',
        id: selectedEmail.id,
        label: selectedEmail.subject,
      },
  );

  useEffect(() => {
    if (emailId) {
      setCurrentView('email');
      setSelectedEmail({ id: emailId });
    }
  }, [emailId]);

  // emailIntegrations
  function handleChangedEmailIntegration(res) {
    const newEmailIntegration = updateOrCreate(emailCredentials, res.data);

    setEmailCredentials(newEmailIntegration);
  }

  function handleRemoveEmailIntegration(emailIntegration) {
    setEmailCredentials((prev) => {
      const newIntegrations = prev.data.filter(
        (e) => e.id !== emailIntegration.id,
      );
      return { ...prev, data: newIntegrations };
    });
  }

  const folderMap = {
    Trash: {
      label: t('Trash'),
      Icon: DeleteIcon,
    },
    'Sent Items': {
      label: t('Sent Items'),
      Icon: SendIcon,
    },
    Spam: {
      label: t('Spam'),
      Icon: BlockIcon,
    },
    Drafts: {
      label: t('Drafts'),
      Icon: DraftsIcon,
    },
    Archive: {
      label: t('Archive'),
      Icon: InventoryIcon,
    },
    INBOX: {
      label: t('Inbox'),
      Icon: InboxIcon,
    },
    Outbound: {
      label: t('Outbound'),
      Icon: ScheduleSendIcon,
    },
    'AI Drafts': {
      label: t('AI Drafts'),
      Icon: AutoAwesomeIcon,
    },
  };

  function syncEmails() {
    DBRequest({
      config,
      path: `emails-sync`,
    });
  }

  // Folder CRUD handlers
  function refreshEmailCredentials() {
    DBRequest({
      config,
      path: 'email-credentials',
      onResponse: (res) => setEmailCredentials(res),
    });
  }

  function handleFolderCreate(newFolder) {
    // Refresh email credentials to get updated folder list
    refreshEmailCredentials();
  }

  function handleFolderDelete(folder) {
    DBRequest({
      config,
      path: `email-folders/${folder.id}`,
      method: API_DELETE(),
      onResponse: () => {
        refreshEmailCredentials();
        // If deleted folder was selected, switch to INBOX
        if (selectedFolder?.id === folder.id) {
          setSelectedFolder({ id: 'S1', name: 'INBOX', type: 'INBOX' });
        }
      },
      onError: () =>
        setAlert({
          type: 'error',
          message: t('Failed to delete folder'),
          open: true,
        }),
    });
  }

  function handleFolderMove(folderId, newParentPath) {
    DBRequest({
      config,
      path: `email-folders/${folderId}/move`,
      method: API_POST({ newParentPath }),
      onResponse: refreshEmailCredentials,
      onError: () =>
        setAlert({
          type: 'error',
          message: t('Failed to move folder'),
          open: true,
        }),
    });
  }

  useEffect(() => {
    // select first folder if no folder is selected
    if (emailCredentials && !selectedFolder) {
      const credentials = emailCredentials.data[0];
      if (!credentials) return;
      const folders = credentials.inbox.folders;
      const newSelectedFolder = folders.find((obj) => obj.name === 'INBOX');
      if (newSelectedFolder)
        setSelectedFolder({ id: 'S1', name: 'INBOX', type: 'INBOX' });
    }
  }, [emailCredentials]);

  // new email
  const [editEmail, setEditEmail] = useState(null);

  // undo send: freshly sent emails that can still be cancelled while the
  // configured send delay (config.mail.outboundEmailSendDelay) is running
  const [undoSendMails, setUndoSendMails] = useState([]);

  function handleNewMail(newMail) {
    // A sent AI draft leaves its folder; the email continues in Outbound.
    if (editEmail?.type === 'aiDraft') {
      emailListRef.current?.deleteEmail(editEmail.oldEmail);
      setSelectedEmail(null);
      fetchFolders();
    }

    setEditEmail(false);

    const delay = Number(config?.mail?.outboundEmailSendDelay) || 0;
    if (delay <= 0 || !newMail?.data) return;

    const mails = Array.isArray(newMail.data) ? newMail.data : [newMail.data];
    const now = Date.now();
    setUndoSendMails((prev) => [
      ...prev,
      ...mails
        .filter((mail) => mail?.id)
        .map((mail) => ({
          ...mail,
          duration: delay * 1000,
          expiresAt: now + delay * 1000,
        })),
    ]);
  }

  function handlePopOut() {
    window.open('/emails', '_blank');
  }

  // handle email change for list and conversation
  function handleEmailChangeForList(email) {
    emailListRef.current.updateEmail(email.data);
  }

  function handleEmailChangeForConversation(email) {
    emailConversationRef.current.updateEmail(email.data);
  }

  function handleEmailDeleteForList(email) {
    setCurrentView('list');
    emailListRef.current.deleteEmail(email);
  }

  function handleMoveEmail(folder, email) {
    handleEmailDeleteForList(email || selectedEmail);

    DBRequest({
      config,
      method: API_POST({ newFolder: folder.id }),
      path: `imap-emails/${email?.id || selectedEmail.id}/move`,
      onError: (err) => {
        handleEmailChangeForList({ data: email || selectedEmail });
        setAlert({
          type: 'error',
          message: t('Failed to move email'),
          open: true,
        });
      },
    });

    setSelectedEmail(null);
  }

  // mobile
  const [currentView, setCurrentView] = useState('folder');

  // dnd
  const [alert, setAlert] = useState({});
  function handleDragStart({ active }) {
    setActiveItem(active);
  }

  function handleDragEnd({ active, over }) {
    setActiveItem(null);

    const activeData = active?.data?.current;
    const overData = over?.data?.current;

    if (!activeData || !overData) return;

    // Email auf Ordner droppen
    // Email objects don't have a type property, so check for email-specific properties
    if (activeData.email && !activeData.type) {
      handleMoveEmail(overData, activeData.email);
      return;
    }

    // Ordner auf Ordner droppen
    if (activeData.type === 'folder' && overData) {
      // Nur innerhalb desselben Kontos
      if (activeData.credentialsId === overData.credentialsId) {
        // Verhindere Drop auf sich selbst
        if (activeData.folder.id !== overData.id) {
          handleFolderMove(activeData.folder.id, overData.path);
        }
      }
    }
  }

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 500,
      tolerance: 5,
      distance: 10,
    },
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  const outBoundStatusMap = {
    0: {
      label: t('Queued'),
      color: colors.info,
      Icon: HourglassTopIcon,
    },
    1: {
      label: t('Sent'),
      color: colors.success,
      Icon: CheckIcon,
    },
    2: {
      label: t('Failed'),
      color: colors.error,
      Icon: ReportGmailerrorredIcon,
    },
    3: {
      label: t('Cancelled'),
      color: colors.grey[500],
      Icon: BlockIcon,
    },
  };

  if (emailCredentials && emailCredentials?.data?.length === 0)
    return (
      <Div100vh className="flex flex-col gap-4 items-center justify-center">
        <img src="/assets/icons/email.webp" className="w-[100px]" />
        <Box
          sx={{ bgcolor: colors.glass }}
          className="rounded-lg p-4 backdrop-blur-lg max-w-[500px]"
        >
          <Typography variant="h3">{t('Welcome to ExoPaper Email')}</Typography>
          <Typography>
            {t(
              'Boost your productivity by linking emails directly to leads, sales and projects. Use shared text modules to streamline communication and save valuable time.',
            )}
          </Typography>
        </Box>
        <Button
          onClick={() => setOpenNewEmail(true)}
          className="w-full max-w-[500px] backdrop-blur-lg"
          variant="contained"
          color="success"
        >
          <AddIcon sx={{ fontSize: '25px' }} />
          {t('Add E-Mail Account')}
        </Button>
        <ExoDialog
          open={openNewEmail}
          onClose={() => setOpenNewEmail(false)}
          limitWidth
          maxWidth="720px"
        >
          <NewEmailIntegration
            onSelect={handleChangedEmailIntegration}
            onClose={() => setOpenNewEmail(false)}
            onDelete={handleRemoveEmailIntegration}
            onChange={handleChangedEmailIntegration}
          />
        </ExoDialog>
      </Div100vh>
    );

  return (
    <Fragment>
      <DragOverlay>
        {activeItem && (
          <>
            {
              <Box sx={{ background: colors.glass, pointerEvents: 'none' }}>
                <EmailCard email={activeItem.data.current.email} />
              </Box>
            }
          </>
        )}
      </DragOverlay>
      {isDesktop && <Box className="flex w-full gap-2 h-full justify-stretch">
        {debug && (
          <Button
            variant="contained"
            color="warning"
            sx={{ position: 'absolute' }}
            className="z-10 bottom-0 left-0"
            onClick={syncEmails}
          >
            sync
          </Button>
        )}
        <Box
          className="rounded-lg flex h-full overflow-hidden backdrop-blur-lg min-w-[500px]"
          sx={{
            bgcolor: colors.glass,
          }}
        >
          <DndContext
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            //onDragMove={handleOnDragMove}
            sensors={sensors}
            collisionDetection={pointerWithin}
            //collisionDetection={closestCenter}
            //collisionDetection={testColDet}
          >
            <ECInboxList
              onSelect={setSelectedFolder}
              selectedFolder={selectedFolder}
              onOpen={() => setOpenNewEmail(true)}
              inboxes={emailCredentials?.data}
              isLoading={!emailCredentials}
              onInboxChange={handleChangedEmailIntegration}
              folderMap={folderMap}
              width="200px"
              onOpenSettings={() => handleOpenSettings()}
              onOpenOutOfOffice={() =>
                handleOpenSettings(EC_SETTINGS_TABS.outOfOffice)
              }
              canPopOut
              hasDnd
              isStandalone={isStandalone}
              onFolderCreate={handleFolderCreate}
              onFolderDelete={handleFolderDelete}
              onFolderMove={handleFolderMove}
            />
            <Box sx={{ bgcolor: colors.card }} className="w-[1px]" />
            <ECEmailsList
              ref={emailListRef}
              inbox={selectedFolder}
              folderMap={folderMap}
              onSelect={setSelectedEmail}
              selectedEmail={selectedEmail}
              selectedFolder={selectedFolder}
              credentials={emailCredentials}
              onFolderChange={setSelectedFolder}
              onNewMail={() => setEditEmail(true)}
              noKeyAction={Boolean(editEmail)}
              onEmailChange={handleEmailChangeForConversation}
              onFolderSelect={setSelectedFolder}
              hasDnd
              outBoundStatusMap={outBoundStatusMap}
            />
          </DndContext>
        </Box>
        <ECEmailConversation
          ref={emailConversationRef}
          selectedEmail={selectedEmail}
          inboxes={emailCredentials?.data}
          selectedFolder={selectedFolder}
          folderMap={folderMap}
          onEdit={setEditEmail}
          onChange={handleEmailChangeForList}
          onDelete={handleEmailDeleteForList}
          onSelect={handleMoveEmail}
          onManageCertificates={() => handleOpenSettings()}
        />
        <ExoAlert
          open={alert?.open}
          onClose={() => setAlert({ ...alert, open: false })}
          alert={alert}
        />
      </Box>}
      {!isDesktop && <Div100vh className="absolute top-0 pt-[64px] pb-[40px] left-0 w-full">
        <Box
          className="h-full sm:h-full flex w-full"
          sx={{
            '.MuiCollapse-wrapperInner, .MuiCollapse-wrapper, .MuiCollapse-root':
              {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              },
          }}
        >
          {currentView === 'folder' && (
            <Box
              className="rounded-lg flex h-full overflow-hidden backdrop-blur-lg w-full"
              sx={{
                bgcolor: colors.glass,
              }}
            >
              <ECInboxList
                onSelect={(e) => {
                  setCurrentView('list');
                  setSelectedFolder(e);
                }}
                selectedFolder={selectedFolder}
                onOpen={() => setOpenNewEmail(true)}
                inboxes={emailCredentials?.data}
                folderMap={folderMap}
                isLoading={!emailCredentials}
                width="100%"
                onOpenSettings={() => handleOpenSettings()}
                onOpenOutOfOffice={() =>
                  handleOpenSettings(EC_SETTINGS_TABS.outOfOffice)
                }
                onInboxChange={handleChangedEmailIntegration}
                isStandalone={isStandalone}
                onFolderCreate={handleFolderCreate}
                onFolderDelete={handleFolderDelete}
                onFolderMove={handleFolderMove}
              />
            </Box>
          )}

          {(currentView === 'list' || currentView === 'email') && (
            <Box
              className="rounded-lg flex h-full overflow-hidden backdrop-blur-lg w-full"
              sx={{
                bgcolor: colors.glass,
              }}
            >
              <ECEmailsList
                ref={emailListRef}
                inbox={selectedFolder}
                folderMap={folderMap}
                onSelect={(e) => {
                  if (!e) return;
                  setCurrentView('email', e);
                  setSelectedEmail(e);
                }}
                selectedEmail={selectedEmail}
                selectedFolder={selectedFolder}
                credentials={emailCredentials}
                onFolderChange={setSelectedFolder}
                onNewMail={() => setEditEmail(true)}
                noKeyAction={Boolean(editEmail)}
                onEmailChange={handleEmailChangeForConversation}
                onFolderSelect={setSelectedFolder}
                isMobile
                onChangeView={setCurrentView}
                outBoundStatusMap={outBoundStatusMap}
              />
            </Box>
          )}
          <ExoDrawer
            open={currentView === 'email'}
            onClose={() => {
              setCurrentView('list');
              navigate('/emails');
            }}
          >
            <Box className="flex h-full overflow-hidden w-full">
              <ECEmailConversation
                ref={emailConversationRef}
                selectedEmail={selectedEmail}
                inboxes={emailCredentials?.data}
                selectedFolder={selectedFolder}
                folderMap={folderMap}
                onEdit={setEditEmail}
                onChange={handleEmailChangeForList}
                onDelete={(email) => {
                  handleEmailDeleteForList(email);
                  setCurrentView('list');
                  navigate('/emails');
                }}
                onSelect={(email) => {
                  handleMoveEmail(email);
                  setCurrentView('list');
                  navigate('/emails');
                }}
                onManageCertificates={() => handleOpenSettings()}
              />
            </Box>
          </ExoDrawer>
        </Box>
      </Div100vh>}
      <ExoDialog
        open={openNewEmail}
        onClose={() => setOpenNewEmail(false)}
        limitWidth
        maxWidth="720px"
      >
        <NewEmailIntegration
          onSelect={handleChangedEmailIntegration}
          onClose={() => setOpenNewEmail(false)}
          onDelete={handleRemoveEmailIntegration}
        />
      </ExoDialog>
      <ExoDialog
        open={Boolean(editEmail)}
        onClose={() => setEditEmail(null)}
        noBlur
        actions={
          <Tooltip title={t('Open in new window')}>
            <IconButton onClick={handlePopOut}>
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        }
      >
        <NewEmail
          blockRendering={Boolean(editEmail)}
          type={editEmail?.type}
          oldEmail={editEmail?.oldEmail}
          credentials={emailCredentials?.data}
          selectedFolder={selectedFolder}
          onNewMail={handleNewMail}
          onAiDraftSaved={(res) => {
            // show the saved edits in the list and the preview behind the editor
            emailListRef.current?.updateEmail(res.data);
            emailConversationRef.current?.updateEmail(res.data);
          }}
        />
      </ExoDialog>
      <ExoDialog
        open={openSettings}
        onClose={() => setOpenSettings(false)}
        limitWidth
        maxWidth="1100px"
      >
        <ECSettings
          initialTab={settingsTab}
          onInboxChange={handleChangedEmailIntegration}
          credentials={emailCredentials?.data}
          setCredentials={setEmailCredentials}
          onDelete={handleRemoveEmailIntegration}
        />
      </ExoDialog>
      <ECUndoSend
        mails={undoSendMails}
        onRemove={(id) =>
          setUndoSendMails((prev) => prev.filter((mail) => mail.id !== id))
        }
      />
    </Fragment>
  );
};
